// import './echo';
// Keep Echo out of the default app bundle. Enable it only on pages that need
// realtime channels so product pages do not pay the Pusher parse/eval cost.

import { initializeAnalytics } from './analytics';
import './product-image-viewer';

initializeAnalytics();
// A visitor may enter through a layout without analytics settings first.
document.addEventListener('livewire:navigated', initializeAnalytics);

(() => {
    if (window.__nriMiniCartInitialized) {
        return;
    }

    window.__nriMiniCartInitialized = true;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
    let activeRoot = null;
    let loaded = false;
    let loading = false;
    let currentCount = 0;
    let currentHtml = null;

    const createRoot = () => {
        const root = document.createElement('div');
        root.className = 'nri-mini-cart';
        root.dataset.miniCartRoot = '';
        root.setAttribute('aria-hidden', 'true');
        root.innerHTML = `
            <button type="button" class="nri-mini-cart__overlay" data-cart-close aria-label="Close cart overlay"></button>
            <aside class="nri-mini-cart__panel" data-mini-cart-panel aria-label="Shopping cart"></aside>
        `;
        document.body.append(root);

        return root;
    };

    const readCartCount = () => {
        const badge = document.querySelector('[data-cart-count]:not(.hidden), [data-cart-count]');
        const text = badge?.textContent?.trim() || '0';

        if (text.endsWith('+')) {
            return Number.parseInt(text, 10) || 0;
        }

        return Number(text) || 0;
    };

    const cartIcon = `
        <svg class="h-6 w-6 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 8h12l-1 12H7L6 8Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
            <path d="M9 8V6a3 3 0 0 1 6 0v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
    `;

    const panelHeader = count => {
        const quantity = Number(count) || 0;
        const label = quantity === 1 ? 'Item' : 'Items';

        return `
        <div class="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-6">
            <div class="flex items-center gap-3 text-[#06275a]">
                ${cartIcon}
                <strong class="text-[18px] font-semibold leading-6">Shipping Cart: ${quantity} ${label}</strong>
            </div>

            <button type="button" class="text-2xl leading-none text-gray-500 transition hover:text-gray-700"
                data-cart-close aria-label="Close cart">
                &times;
            </button>
        </div>
    `;
    };

    const emptyCartHtml = () => `
        <div class="flex h-full flex-col overflow-hidden bg-white">
            ${panelHeader(0)}
            <div class="min-h-0 flex-1 overflow-y-auto px-0 py-0">
                <div class="mx-5 mt-5 rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-gray-500">
                    <p>Your shopping cart is empty. Start shopping.</p>
                </div>
            </div>
        </div>
    `;

    const skeletonCartHtml = count => `
        <div class="flex h-full flex-col overflow-hidden bg-white">
            ${panelHeader(count)}
            <div class="min-h-0 flex-1 overflow-y-auto px-0 py-0">
                ${Array.from({ length: Math.min(Math.max(count, 1), 3) }).map(() => `
                    <div class="flex min-w-0 gap-3 border-b border-gray-200 bg-white px-5 py-5">
                        <div class="flex shrink-0 flex-col items-center gap-2">
                            <span class="nri-mini-cart-skeleton-block" style="height:2rem;width:2rem"></span>
                            <span class="nri-mini-cart-skeleton-block" style="height:1.25rem;width:1.25rem"></span>
                            <span class="nri-mini-cart-skeleton-block" style="height:2rem;width:2rem"></span>
                        </div>
                        <div class="min-w-0 flex-1 pt-3">
                            <span class="nri-mini-cart-skeleton-block mb-2" style="height:1rem;width:100%"></span>
                            <span class="nri-mini-cart-skeleton-block mb-4" style="height:1rem;width:66.666667%"></span>
                            <span class="nri-mini-cart-skeleton-block" style="height:1.25rem;width:5rem"></span>
                        </div>
                    </div>
                `).join('')}
            </div>
            <div class="shrink-0 border-t border-gray-200 bg-white px-4 py-4">
                <div class="mb-4 flex items-center justify-between">
                    <span class="nri-mini-cart-skeleton-block" style="height:1rem;width:4rem"></span>
                    <span class="nri-mini-cart-skeleton-block" style="height:1.5rem;width:6rem"></span>
                </div>
                <div class="space-y-3">
                    <a href="/cart"
                        class="flex h-11 w-full items-center justify-center rounded-md border border-gray-300 bg-white text-sm font-medium text-[#1f2a44] transition hover:bg-gray-50"
                        data-cart-close>
                        View Cart
                    </a>

                    <a href="/checkout/step1"
                        class="flex h-11 w-full items-center justify-center rounded-md bg-[var(--nri-blue)] text-sm font-semibold text-white transition hover:bg-[#09567f]">
                        Checkout Now
                    </a>
                </div>
            </div>
        </div>
    `;

    const setPanelHtml = html => {
        const panel = getPanel();

        if (!panel) {
            return;
        }

        currentHtml = html;
        panel.innerHTML = html;
        loaded = true;
    };

    const getRoot = (shouldCreate = false) => {
        const root = document.querySelector('[data-mini-cart-root]') || (shouldCreate ? createRoot() : null);

        if (root !== activeRoot) {
            activeRoot = root;
            loading = false;

            const panel = root?.querySelector('[data-mini-cart-panel]');

            if (panel && currentHtml !== null) {
                panel.innerHTML = currentHtml;
                loaded = true;
            } else {
                loaded = false;
            }
        }

        return root;
    };

    const getPanel = (shouldCreate = false) => getRoot(shouldCreate)?.querySelector('[data-mini-cart-panel]') || null;

    const setCartCount = count => {
        const value = Number(count) || 0;
        currentCount = value;

        document.querySelectorAll('[data-cart-count]').forEach(badge => {
            badge.textContent = value > 99 ? '99+' : String(value);
            badge.classList.toggle('hidden', value <= 0);
            badge.classList.toggle('flex', value > 0);
        });
    };

    const applyCartResponse = data => {
        if (typeof data.html === 'string') {
            setPanelHtml(data.html);
        }

        if (typeof data.count !== 'undefined') {
            setCartCount(data.count);
        }
    };

    const requestCart = async (url, options = {}) => {
        const response = await fetch(url, {
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                ...(options.body ? { 'Content-Type': 'application/json' } : {}),
            },
            ...options,
        });

        const data = await response.json();
        applyCartResponse(data);

        if (!response.ok) {
            throw new Error('Cart request failed.');
        }

        return data;
    };

    const loadCart = async () => {
        if (!getPanel(true) || loaded || loading) {
            return;
        }

        if (currentCount <= 0) {
            setPanelHtml(emptyCartHtml());

            return;
        }

        const panel = getPanel(true);
        if (panel) {
            panel.innerHTML = skeletonCartHtml(currentCount);
        }

        loading = true;

        try {
            await requestCart('/cart/mini');
        } finally {
            loading = false;
        }
    };

    const openCart = () => {
        const root = getRoot(true);

        if (!root) {
            return;
        }

        root.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden', 'nriparts-cart-open');
        currentCount = readCartCount();
        void loadCart();

        requestAnimationFrame(() => {
            root.classList.add('is-open');
        });
    };

    const closeCart = () => {
        const root = getRoot();

        if (root) {
            root.classList.remove('is-open');
            root.setAttribute('aria-hidden', 'true');
        }

        document.body.classList.remove('overflow-hidden', 'nriparts-cart-open');
    };

    document.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : event.target.parentElement;

        if (!target) {
            return;
        }

        const openButton = target.closest('[data-cart-open]');

        if (openButton) {
            event.preventDefault();
            openCart();

            return;
        }

        const closeButton = target.closest('[data-cart-close]');

        if (closeButton) {
            if (closeButton.tagName !== 'A') {
                event.preventDefault();
            }

            closeCart();

            return;
        }

        const quantityButton = target.closest('[data-cart-quantity][data-cart-item-url]');

        if (quantityButton) {
            event.preventDefault();
            quantityButton.disabled = true;

            requestCart(quantityButton.dataset.cartItemUrl, {
                method: 'PATCH',
                body: JSON.stringify({ quantity: Number(quantityButton.dataset.cartQuantity) || 0 }),
            }).catch(() => {
                quantityButton.disabled = false;
            });

            return;
        }

        const removeButton = target.closest('[data-cart-remove-url]');

        if (removeButton) {
            event.preventDefault();
            removeButton.disabled = true;

            requestCart(removeButton.dataset.cartRemoveUrl, {
                method: 'DELETE',
            }).catch(() => {
                removeButton.disabled = false;
            });
        }
    });

    document.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
            closeCart();
        }
    });

    window.addEventListener('cart-item-added', event => {
        if (typeof event.detail?.cartCount !== 'undefined') {
            setCartCount(event.detail.cartCount);
        }

        loaded = false;
        currentHtml = null;
        openCart();
    });

    window.addEventListener('nriparts-cart-updated', () => {
        loaded = false;
        currentHtml = null;

        void requestCart('/cart/mini').catch(() => {
            currentCount = readCartCount();
        });
    });

    document.addEventListener('livewire:navigated', () => {
        getRoot();
        currentCount = readCartCount();

        if (currentCount <= 0) {
            currentHtml = emptyCartHtml();
            loaded = false;
        }
    });

    currentCount = readCartCount();
})();
